import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Link } from "react-router-dom";

import { useReducedMotion } from "../hooks/useReducedMotion.js";
import { canRenderStage } from "../lib/reelCapability.js";
import {
  COMPACT_BREAKPOINT,
  FRAME_WIDTH_MAX,
  frameWidth,
  projectReel,
} from "./three/reelGeometry.js";
import { formatRating } from "../lib/format.js";
import type { ReviewSummary } from "../lib/types.js";
import { Rating } from "./Rating.js";
import { ReviewCard } from "./ReviewCard.js";

const ReelJourney = lazy(() => import("./three/ReelJourney.js"));

/**
 * Scroll distance, in viewport heights, spent rolling each frame through the
 * gate. Under about half a screen the frames pass faster than the stills can be
 * read; much over one and the section feels like it has trapped the reader.
 */
const SCREENS_PER_FRAME = 0.7;

/**
 * The same, for a phone.
 *
 * Shorter because a scroll gesture is shorter. A flick on a trackpad or a
 * mouse wheel covers far more of the page than a thumb-swipe does, so 0.7
 * screens per frame that reads as unhurried on a laptop turns six reviews into
 * five hundred vertical percent of thumbing on a handset. At 0.5 the section is
 * a little over three screens and each frame still holds the gate long enough
 * to be read.
 */
const SCREENS_PER_FRAME_COMPACT = 0.5;

/**
 * Sprocket rail width and stock gauge, as fractions of the frame's width.
 *
 * Both were fixed lengths in the stylesheet — `2.35rem` and `16px` — measured
 * against a frame that was always 430px wide. Now that the frame narrows to fit
 * the viewport those have to narrow with it, or a phone gets a 345px frame with
 * 75px of rail down the sides and the picture is squeezed to less than what is
 * left. The two ratios below are exactly what the old fixed values were at
 * 430px, so nothing changes on a wide screen.
 */
const RAIL_RATIO = 37.6 / 430;
const GAUGE_RATIO = 16 / 430;


/**
 * How much of the pitch a frame's picture occupies.
 *
 * The rest is the bar of bare stock between one frame and the next. Height is
 * *derived* rather than fixed, because the pitch is the drum's circumference
 * divided by the number of cells — so a hardcoded height silently overlaps the
 * frames the moment the cell count changes, which is exactly what adding the
 * leader did.
 */
const FRAME_FILL = 0.9;

/**
 * Fallback drum radius, in pixels.
 *
 * Only used for the very first paint, before the viewport has been measured.
 * The real radius is computed from the reel's projected geometry — see
 * `projectReel` — so the film is wound onto the wheel as rendered rather than
 * onto a guess about where it might be.
 */
const FALLBACK_RADIUS = 330;

/**
 * How far round the drum a frame stays visible, in degrees.
 *
 * Must stay under 90. The film is DOM and the wheel is WebGL on a layer behind
 * it, and the browser cannot occlude one with the other — so a frame past the
 * quarter turn is on the *far side* of the drum and yet still paints straight
 * over the front of it. At 105 that is exactly what was happening, which is
 * why the frames read as pasted in front of the reel rather than wound onto it.
 */
const VISIBLE_ARC = 88;

/**
 * How far the film turns while the reel is travelling in from the hero.
 *
 * Exactly one revolution, and that is not arbitrary: the gate is wherever the
 * drum's angle lands on a multiple of the frame pitch, so a whole turn puts the
 * film back on the same frame it started from. Any other amount and the section
 * would open part-way between two frames.
 *
 * The point of turning at all during the approach is that the reel and its film
 * are one object — a wheel that coasts in dead still and only starts turning
 * once a section boundary is crossed reads as two separate tricks.
 */
const APPROACH_TURN = 360;

/**
 * Blank cells wound on before the first review and after the last.
 *
 * Real film has leader on it — unexposed stock at the head and the tail, so the
 * picture never runs right to the end of the reel.
 *
 * Both ends matter, and only having a tail was not enough. The roll is a closed
 * loop, so the tail is geometrically also the head — but the section opens with
 * the first review already at the gate, which put that blank stock behind the
 * reader before they ever saw it. There was no run-in. A lead-in section means
 * the film arrives *as film*, with a stretch of empty tape ahead of the first
 * picture.
 *
 * They double as the obvious place for the reel to grow: as Davis writes more,
 * reviews take these slots and the leader stays where it is.
 */
/*
 * One cell each end rather than two.
 *
 * Every cell on the drum takes a share of the same circumference, so leader is
 * paid for directly out of how tall each *review* can be — four blank cells
 * were costing the pictures about a fifth of their height. One at each end
 * still gives the film a run-in and a tail, which was the point of having any.
 */
const LEAD_IN = 1;
const LEAD_OUT = 1;

/**
 * Point in the approach at which the film starts threading on.
 *
 * The approach runs 0 → 1 over the viewport-height of scrolling before the
 * section pins, and this is deliberately late in it: the reel has crossed the
 * screen, turned onto its rim and settled before there is any tape on it at
 * all. The film then threads through over the remainder, so it is fully on by
 * the time the first review reaches the gate.
 */
const REVEAL_FROM = 0.72;

/**
 * How much of a viewport the reel takes to clear the screen at the end.
 *
 * The canvas is fixed, so it does not scroll away with the section — left
 * alone it stayed on top of the featured review and the cards below it, a
 * giant gold wheel behind the copy with no way to get rid of it. This is the
 * distance over which it hands the page back.
 */
const EXIT_SPAN = 0.55;

interface ReelScrollerProps {
  reviews: ReviewSummary[];
}

/**
 * The reel's film, rolling down the page.
 *
 * The hero shows the reel face-on with its film wound round the rim. Scroll,
 * and the hero clears; the reel turns side-on so you are looking at that rim
 * edge, and the film feeds off it and **rolls downward** past a fixed gate —
 * the way film actually runs through a projector. Each frame carries one
 * review's still, its title and its score. Whichever frame is at the gate opens
 * into a review box with a link to the full piece.
 *
 * ## Why it rolls rather than spins
 *
 * An earlier build put the frames on a turntable and rotated them past the
 * viewer. It was wrong twice over: film does not orbit a vertical axis, and the
 * cells nearest the viewer were always turning away, so the one you wanted to
 * read was never square to you. Now the frames travel in a straight line down
 * the screen and curve back over the rollers at each end, which is both what
 * film does and what puts the frame you care about flat in front of you.
 *
 * ## How the scroll is wired
 *
 * The section is taller than the viewport and its stage is `sticky`, so the
 * browser does the pinning natively — no scroll hijacking, no `preventDefault`
 * on wheel events. Scroll position maps to how far the film has fed; the reader
 * can flick through it, drag the scrollbar or page straight past, and all three
 * behave.
 *
 * Frame transforms are written straight to the DOM inside a rAF-coalesced
 * handler. Only the index of the frame at the gate goes through React state,
 * and that changes a few times per section rather than sixty times a second.
 */
export function ReelScroller({ reviews }: ReelScrollerProps) {
  const reduced = useReducedMotion();
  // Decided during the first render, not in an effect afterwards. Deciding
  // late would paint the fallback grid and then swap a multi-screen section in
  // underneath the reader, moving everything below it.
  const [capable] = useState(canRenderStage);
  const [active, setActive] = useState(0);

  const enabled = capable && !reduced;

  const section = useRef<HTMLElement>(null);
  const reelLayer = useRef<HTMLDivElement>(null);
  const pin = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const drum = useRef<HTMLDivElement>(null);
  const frames = useRef<(HTMLDivElement | null)[]>([]);
  const progress = useRef(0);
  /** 0 while the hero owns the reel, 1 once it has turned onto its rim. */
  const approach = useRef(0);
  /** The film's rotation, in degrees. Shared with the reel so they lock. */
  const feed = useRef(0);
  /** 0 while the reel owns the screen, 1 once it has handed the page back. */
  const exit = useRef(0);

  const count = reviews.length;
  /**
   * Cells wound on the drum: every review, plus the blank leader.
   *
   * The pitch is set by this rather than by the review count, so adding leader
   * spaces the whole roll out instead of overlapping the pictures — and the
   * scroll still ends on the last *review*, with the blank stock carrying on
   * past it.
   */
  const cells = count + LEAD_IN + LEAD_OUT;
  /** Angle between one cell and the next around the drum. */
  const step = cells > 0 ? 360 / cells : 0;

  /**
   * Every cell on the drum, in the order they are wound on.
   *
   * Built as one list rather than rendered as three loops so a cell's index
   * *is* its position on the drum — which is what every angle below is computed
   * from. Splitting it would mean keeping three separate index offsets in step
   * with each other by hand.
   */
  const cellList = useMemo<(ReviewSummary | null)[]>(
    () => [
      ...Array.from({ length: LEAD_IN }, () => null),
      ...reviews,
      ...Array.from({ length: LEAD_OUT }, () => null),
    ],
    [reviews],
  );

  /**
   * Where the reel actually is, in pixels, for this viewport.
   *
   * Derived from the same camera and scale the WebGL reel uses, so the film's
   * cylinder has the rendered rim's radius and shares its axle. Recomputed on
   * resize only — it is a function of viewport height and nothing else, so it
   * has no business running on scroll.
   */
  const [projection, setProjection] = useState(() =>
    typeof window === "undefined"
      ? { radius: FALLBACK_RADIUS, perspective: 1400, centreOffsetY: 0 }
      : projectReel(window.innerWidth, window.innerHeight),
  );

  /**
   * Width of one frame, clamped to the viewport.
   *
   * State rather than a constant because it is a function of viewport width,
   * which changes when a phone is rotated. Recomputed by the same `measure`
   * that recomputes the projection, so the drum's width and its radius can
   * never be measured against different viewports.
   */
  const [filmWidth, setFilmWidth] = useState(() =>
    typeof window === "undefined"
      ? FRAME_WIDTH_MAX
      : frameWidth(window.innerWidth, window.innerHeight),
  );

  /**
   * Whether this is the stacked, one-column layout.
   *
   * Read once and deliberately not updated on resize: it sets the section's
   * *height*, and a section that grows or shrinks under a reader who is part
   * way down it moves every frame's scroll position at once. Rotating a phone
   * mid-section is rare; the film jumping half a screen when you do is not
   * something to trade for it.
   */
  const [compact] = useState(
    () => typeof window !== "undefined" && window.innerWidth < COMPACT_BREAKPOINT,
  );

  const screensPerFrame = compact
    ? SCREENS_PER_FRAME_COMPACT
    : SCREENS_PER_FRAME;

  /**
   * Height of one frame, from the arc it occupies on the drum.
   *
   * pitch = circumference / cells. A frame taller than its pitch physically
   * cannot fit — it runs into its neighbour, which is what made the pictures
   * overlap once the leader changed the cell count.
   */
  const frameHeight =
    cells > 0
      ? Math.round(((2 * Math.PI * projection.radius) / cells) * FRAME_FILL)
      : 0;

  /**
   * The film container's offset and height inside the pinned panel.
   *
   * Both are fixed by the layout — a heading of known height above, a review
   * box below — so they are measured once per resize and reused on every scroll
   * frame. Measuring them per frame is what forced a synchronous layout on
   * every scroll event.
   */
  const stageOffset = useRef(0);
  const stageHeight = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const measure = () => {
      setProjection(projectReel(window.innerWidth, window.innerHeight));
      setFilmWidth(frameWidth(window.innerWidth, window.innerHeight));

      const box = stage.current?.getBoundingClientRect();
      const panelBox = pin.current?.getBoundingClientRect();
      if (!box || !panelBox) return;

      stageOffset.current = box.top - panelBox.top;
      stageHeight.current = box.height;
    };

    measure();
    window.addEventListener("resize", measure);
    // The panel has not laid out on the first effect tick.
    const settle = window.setTimeout(measure, 0);

    return () => {
      window.clearTimeout(settle);
      window.removeEventListener("resize", measure);
    };
  }, [enabled]);

  /**
   * Turns the drum to the current feed position.
   *
   * The drum rotates about X, so its surface travels vertically — the frames
   * wound on it roll downward past the gate exactly as film does over a
   * roller. `offset` is the fractional frame at the gate; everything else is a
   * function of each frame's angle from there.
   */
  const layout = useCallback((sectionTop: number) => {
    const node = drum.current;
    const panel = stage.current;
    if (!node || !panel || count === 0) return;

    /*
     * Where the reel's axle falls inside the film's own container.
     *
     * The canvas is fixed to the viewport, so the axle is at the viewport's
     * centre plus the reel's own world offset. The film's container is
     * somewhere else — under a heading, inside a sticky panel that is still
     * travelling until it pins.
     *
     * Derived rather than measured. `getBoundingClientRect` here forced a
     * synchronous layout on every scroll frame, which is exactly the kind of
     * read-after-write that makes scrolling feel heavy. The container's offset
     * *inside* the panel is fixed, so it is cached once; the panel's own
     * position follows from the section's, which the caller has already read:
     * a `top: 0` sticky panel sits at the section's top until that goes
     * negative, and is pinned to zero from then on.
     */
    const panelTop = Math.max(0, sectionTop);
    const boxTop = panelTop + stageOffset.current;
    const boxHeight = stageHeight.current;

    const cameraAxis = window.innerHeight / 2 + projection.centreOffsetY;
    const originY = cameraAxis - boxTop;
    // The drum is centred in its container, so it only needs the difference.
    const axleY = originY - boxHeight / 2;

    // The CSS vanishing point has to sit where the camera's optical axis
    // crosses this container. With it left at the container's own centre the
    // radius is right but the cylinder is projected from the wrong eye
    // position, and the film skews off the rim.
    panel.style.perspectiveOrigin = `50% ${originY}px`;

    /*
     * One angle drives everything: the drum here, and the reel in the canvas,
     * which reads it from `feed`. They are the same wheel, so a single number
     * is the only way they can be guaranteed not to drift apart.
     *
     * The approach term is what keeps the film turning while the reel is still
     * crossing the screen, rather than sitting frozen until the section pins.
     */
    const angle =
      LEAD_IN * step +
      approach.current * APPROACH_TURN +
      progress.current * (count - 1) * step;

    feed.current = angle;

    /*
     * How much of the film has threaded on.
     *
     * The tape does not exist until the wheel has arrived. While the reel is
     * still crossing the screen and turning onto its rim there is nothing on
     * it — bare metal — and the film only threads through once it is in place.
     * Showing both at once made the arrival read as two things sliding into
     * position beside each other rather than as one object being loaded.
     *
     * The window is the last stretch of the approach, so the film is fully on
     * by the moment the section pins and the first review reaches the gate.
     */
    const threading = Math.min(
      1,
      Math.max(0, (approach.current - REVEAL_FROM) / (1 - REVEAL_FROM)),
    );

    /*
     * Smoothstep on both ends.
     *
     * A linear ramp starts and stops at full speed, which is what made the
     * hand-offs between sections feel like cuts. Easing in and out of each one
     * is the difference between the film arriving and the film being switched
     * on.
     */
    const ease = (t: number) => t * t * (3 - 2 * t);

    // On at the start, off at the end — one number for how present the film is.
    const reveal = ease(threading) * (1 - ease(exit.current));

    /*
     * The whole reel layer goes with it. The film is DOM and fades per frame,
     * but the wheel is a canvas on a fixed layer of its own, and it has to
     * leave at the same rate or the tape disappears off a wheel that stays.
     */
    const layer = reelLayer.current;
    if (layer) {
      const shown = 1 - ease(exit.current);
      layer.style.opacity = String(shown);
      // Fully gone: stop compositing a full-viewport canvas behind the rest of
      // the page for the entire time the reader is below it.
      layer.style.visibility = shown <= 0.01 ? "hidden" : "visible";
    }

    /*
     * Negated, so the film runs *downwards*.
     *
     * With a positive angle here the next frame arrives from below and the
     * strip travels up the screen — backwards for film coming off a reel
     * mounted above it. Flipping this (and the sign each frame is wound on
     * with) sends the picture the way it actually goes through a projector.
     */
    node.style.transform =
      `translate3d(0, ${axleY}px, -${projection.radius}px) rotateX(${-angle}deg)`;

    for (const [index, frame] of frames.current.entries()) {
      if (!frame) continue;

      // Angular distance from the gate, wrapped to the shorter way round.
      let away = Math.abs(index * step - angle) % 360;
      if (away > 180) away = 360 - away;

      // Nothing on the reel yet, or this cell is round the back: skip the work
      // entirely rather than compositing something nobody can see.
      if (reveal === 0 || away > VISIBLE_ARC) {
        frame.style.visibility = "hidden";
        continue;
      }

      frame.style.visibility = "visible";

      /*
       * Opaque at the gate and either side of it — film is not translucent, and
       * fading it there is what made the reel show straight through the
       * picture. This fade only handles cells turning away over the top and
       * bottom of the drum, where it stands in for them leaving the light.
       *
       * Multiplied by `reveal`, which is what threads the whole strip on.
       */
      const lit =
        away <= step / 2
          ? 1
          : Math.max(0, 1 - (away - step / 2) / (VISIBLE_ARC - step / 2));

      frame.style.opacity = String(lit * reveal);

      /*
       * A focus pull as it threads.
       *
       * The film arrives soft and dim and resolves as it settles onto the reel,
       * which reads as it coming into the gate's light rather than as a
       * rectangle being faded up. Cleared to `none` once fully on: a filter on
       * a composited layer is not free, and there is no reason to keep paying
       * for one that is doing nothing.
       */
      frame.style.filter =
        reveal >= 1
          ? "none"
          : `blur(${((1 - reveal) * 7).toFixed(1)}px) brightness(${(0.35 + 0.65 * reveal).toFixed(2)})`;
    }
  }, [count, step, projection.radius, projection.centreOffsetY]);

  useEffect(() => {
    if (!enabled) return;

    let queued = false;

    const read = () => {
      queued = false;

      const node = section.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      const next = travel <= 0 ? 0 : Math.min(1, Math.max(0, -rect.top / travel));

      /*
       * The approach runs over the viewport-height of scrolling immediately
       * before the section pins. That is the stretch where the hero is leaving,
       * so the reel crosses the screen and turns onto its rim exactly as the
       * copy it was sitting beside scrolls away.
       */
      const viewport = window.innerHeight;
      approach.current = Math.min(
        1,
        Math.max(0, (viewport - rect.top) / viewport),
      );

      /*
       * The exit runs from the moment the section's bottom edge reaches the
       * bottom of the screen — which is exactly when the sticky panel unpins
       * and the page starts moving on — to a little over half a viewport later.
       *
       * Without it the reel simply stayed: the canvas is fixed, so it does not
       * travel away with its section, and it sat over the featured review and
       * every card below.
       */
      exit.current = Math.min(
        1,
        Math.max(0, (viewport - rect.bottom) / (viewport * EXIT_SPAN)),
      );

      progress.current = next;
      layout(rect.top);

      if (count > 0) setActive(Math.round(next * (count - 1)));
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(read);
    };

    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enabled, layout, count]);

  if (count === 0) return null;

  // Reduced motion, a small viewport or no WebGL: the same reviews, as cards.
  // Not a degraded version of the feature — the feature is the reviews, and the
  // reel is how they are presented when presentation is affordable.
  if (!enabled) {
    return (
      <section
        aria-labelledby="reel-heading"
        className="mx-auto max-w-6xl px-5 py-16"
      >
        <ReelHeading />

        <div className="mt-8 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      </section>
    );
  }

  const current = reviews[active];

  return (
    <section
      ref={section}
      aria-labelledby="reel-heading"
      /*
        `svh`, not `vh`.

        On a phone `100vh` is the viewport with the browser's own chrome hidden,
        which is not the viewport you have while you are scrolling — the URL bar
        is still there for the first flick. `svh` is the smaller, settled figure
        and, unlike `dvh`, it does not change as the bar collapses. A section
        whose height changed mid-scroll would slide every frame's position out
        from under the reader. On a desktop the three units are identical.
      */
      style={{ height: `${100 + count * screensPerFrame * 100}svh` }}
      className="relative"
    >
      {/*
        The reel, on its own fixed layer.

        Fixed rather than parented to this section because it has to be on
        screen before the section is: it starts life over in the hero, eases
        across and turns as the hero leaves, and only then does the film start
        feeding. One canvas, one wheel, the whole way down — which is the point.
        `-z-10` keeps it behind the film and the copy.
      */}
      <div ref={reelLayer} className="pointer-events-none fixed inset-0 -z-10">
        <Suspense fallback={null}>
          <ReelJourney approach={approach} feed={feed} />
        </Suspense>
      </div>

      {/*
        `pt-20` clears the sticky site header, which the stage scrolls under.

        `h-dvh` rather than `h-screen`: every measurement in `layout` is taken
        against `window.innerHeight`, and on a phone that is the *dynamic*
        viewport — what is visible right now, with or without the URL bar.
        `h-screen` is `100vh`, the viewport with the bar gone, so the panel was
        taller than the space the maths thought it had and the drum's axle sat
        low by however tall the bar is.
      */}
      <div
        ref={pin}
        className="sticky top-0 flex h-dvh flex-col overflow-hidden pt-20"
      >
        <div className="mx-auto w-full max-w-6xl px-5">
          <ReelHeading />
        </div>

        <div className="relative flex-1">
          {/*
            The film, wound on a drum.

            `perspective` sits on this element so every frame shares one
            vanishing point — that is what makes them read as one continuous
            length of film wrapped round a wheel rather than as separate tilted
            cards. The drum inside rotates about X, so its surface travels
            vertically and the film rolls downward past the gate.
          */}
          <div
            ref={stage}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              perspective: `${projection.perspective}px`,
            }}
          >
            <div
              ref={drum}
              className="relative"
              style={
                {
                  width: filmWidth,
                  height: frameHeight,
                  transformStyle: "preserve-3d",
                  // Inherited by every frame wound on this drum. See the note
                  // beside `.film-frame-face` in index.css.
                  "--rail": `${(filmWidth * RAIL_RATIO).toFixed(1)}px`,
                  "--gauge": `${Math.max(9, Math.round(filmWidth * GAUGE_RATIO))}px`,
                  // The caption's scrim is a share of the frame it sits in, not
                  // a fixed 96px. On a phone the frames are a little over half
                  // the height they are on a laptop, and a fixed scrim there
                  // covered most of the still it was supposed to be protecting
                  // the caption from.
                  "--scrim": `${Math.round(frameHeight * 0.34)}px`,
                } as CSSProperties
              }
            >
              {cellList.map((review, index) => {
                /*
                 * A cell's index is its position on the drum, and the roll now
                 * opens with blank leader — so the review's own index, which is
                 * what `active` and the review box speak in, is this minus the
                 * lead-in.
                 */
                const reviewIndex = index - LEAD_IN;
                const isActive = review !== null && reviewIndex === active;

                return (
                  <div
                    key={review ? review.id : `leader-${index}`}
                    ref={(node) => {
                      frames.current[index] = node;
                    }}
                    aria-hidden={!isActive}
                    className="film-frame absolute inset-0"
                    style={{
                      transformStyle: "preserve-3d",
                      // Wound onto the drum: turned to its own angle around the
                      // axle, then pushed out to the rim.
                      transform: `rotateX(${index * step}deg) translateZ(${projection.radius}px)`,
                    }}
                  >
                    {/*
                      The stock's back face, held one thickness behind the
                      front. Film has a gauge; without this the frames are
                      zero-depth planes and give themselves away the moment
                      they tilt.
                    */}
                    <div aria-hidden="true" className="film-frame-gauge" />

                    {review ? (
                      <Frame review={review} active={isActive} />
                    ) : (
                      <BlankFrame />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/*
            No fade overlays here.
            ---------------------
            Two gradients used to sit over this area to stop the roll running
            into the heading and the review box. They were drawing a hard
            horizontal band straight across the wheel: they begin at a fully
            opaque `ink-900`, and the reel is on a layer *behind* the page, so
            an opaque strip at the top of this box cut the reel in half at a
            fixed screen position — the clipping that looked like a rendering
            bug on scroll.

            They are also no longer doing a job. When the film was a flat strip
            it really did run past this box; wound on a drum it cannot, because
            every frame sits on a circle of the reel's own radius and anything
            past `VISIBLE_ARC` is hidden outright. The geometry confines it, so
            nothing needs to be painted over the top.

            The review box keeps its own wash, which is the one place a frame
            can still reach.
          */}
        </div>

        {/*
          The review box, held still below the gate rather than riding on the
          frame. Text that bends away in perspective is text nobody reads, and
          this is the part with the link in it.
        */}
        {current && (
          // `z-10` and its own wash: a frame rolling out of the gate travels
          // past the bottom of the film area, and a 3D-transformed element is
          // not clipped by its parent's box, so without this the tail of the
          // roll drifts across the review text.
          <div className="relative z-10 bg-gradient-to-t from-ink-900 via-ink-900/95 to-transparent">
            <div className="mx-auto w-full max-w-6xl px-5 pb-10 pt-6">
              <ReviewBox review={current} position={active + 1} of={count} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ReelHeading() {
  return (
    <h2
      id="reel-heading"
      className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-paper-500"
    >
      <span className="h-px w-8 bg-lamp-400" aria-hidden="true" />
      On the reel
    </h2>
  );
}

/**
 * One frame of film: gold stock, sprocket rails down both edges, and the still
 * sitting in the window between them.
 */
function Frame({ review, active }: { review: ReviewSummary; active: boolean }) {
  return (
    <div className="film-frame-face">
      <div className="film-frame-window relative h-full w-full overflow-hidden bg-ink-900">
        {review.coverImageUrl ? (
          <img
            src={review.coverImageUrl}
            alt={`Still from ${review.filmTitle}`}
            // Eager, not lazy. Every frame is inside the viewport from the
            // moment the section mounts — it is a 3D transform that moves them,
            // and transforms do not move an element's layout box, so the
            // browser's lazy heuristic never fires and frames roll into the
            // gate still blank. There are only a handful of these.
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-ink-800 to-ink-700"
          >
            <span className="font-display text-6xl text-ink-500">
              {review.filmTitle.slice(0, 1).toUpperCase()}
            </span>
          </div>
        )}

        {/* Keeps the caption legible over a bright still. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-950/95 to-transparent"
          style={{ height: "var(--scrim, 6rem)" }}
        />

        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4">
          <p className="font-display text-lg leading-tight text-paper-50">
            {review.filmTitle}
            {review.filmYear && (
              <span className="ml-2 text-sm text-paper-400">{review.filmYear}</span>
            )}
          </p>

          {review.rating !== null && (
            <span className="shrink-0 rounded-sm bg-lamp-400 px-2 py-0.5 text-sm font-bold tabular-nums text-ink-950">
              {formatRating(review.rating)}
            </span>
          )}
        </div>

        {/* The gate's light falls off at the edges of the frame in the gate. */}
        {active && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-lamp-300/35"
          />
        )}
      </div>
    </div>
  );
}

/**
 * A blank cell of leader.
 *
 * Same stock and the same sprocket rails as a picture frame, with an unexposed
 * window in place of the still. The faint mark is the brand's own asterisk at
 * very low contrast — enough that the cell does not read as a rendering
 * failure, not enough to look like content.
 */
function BlankFrame() {
  return (
    <div className="film-frame-face">
      <div className="film-frame-window relative flex h-full w-full items-center justify-center overflow-hidden bg-ink-950">
        <span aria-hidden="true" className="text-2xl text-ink-700">
          ✳
        </span>
      </div>
    </div>
  );
}

function ReviewBox({
  review,
  position,
  of,
}: {
  review: ReviewSummary;
  position: number;
  of: number;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-6 border-t border-ink-700 pt-5">
      <div className="min-w-0 max-w-2xl">
        {/*
          A shadow rather than a heavier wash behind the whole box. The tail of
          the roll deliberately runs past the gate and over the top of this
          panel — that is the film leaving, and painting it out would cost the
          effect. What it must not do is take the label with it, and on a short
          phone the frame directly under the gate lands right on this line.
        */}
        <p className="text-[0.7rem] uppercase tracking-[0.28em] text-lamp-400 [text-shadow:0_1px_4px_rgb(0_0_0/0.9)]">
          Frame {position} of {of}
        </p>

        {/*
          `aria-live`: the heading changes as the reader scrolls, and without an
          announcement a screen reader user paging through the section with
          Space would hear nothing change at all.
        */}
        <h3
          aria-live="polite"
          className="mt-2 font-display text-3xl leading-tight text-paper-50 [text-shadow:0_1px_6px_rgb(0_0_0/0.8)]"
        >
          {review.title}
        </h3>

        {review.excerpt && (
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-paper-300">
            {review.excerpt}
          </p>
        )}
      </div>

      <div className="flex items-center gap-5">
        <Rating value={review.rating} size="sm" />

        <Link
          to={`/reviews/${review.slug}`}
          className="shrink-0 rounded-sm bg-lamp-400 px-5 py-2.5 text-sm font-semibold text-ink-950 transition-transform hover:-translate-y-0.5 hover:bg-lamp-300"
        >
          Read full review
        </Link>
      </div>
    </div>
  );
}
